import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { useState } from 'react'
import { LoaderIcon } from 'react-hot-toast'
import { BsSearch } from 'react-icons/bs'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useInfiniteQuery, useMutation } from 'react-query'

import { privateRequest } from 'config/axios.config'
import queryClient from 'config/queyClient.config'
import { dateFormatter } from 'utils'
import { errorHandler } from 'utils/errorHandler'
import useDebounce from 'utils/useDebounce'

import NoData from 'components/NoData'
import Button from 'components/form/button'
import Input from 'components/form/input'
import addUpdateChiefComplaint from 'features/chief-complaints/addUpdateChiefComplaint.modal'

interface DeletePayload {
    id: string
}

export default function ChiefComplaintsPage() {
    const addUpdateChiefComplaints = useModal(addUpdateChiefComplaint)
    const [search, setSearch] = useState('')
    const debouncedSearchTerm = useDebounce(search, 500)

    const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery<
        ChiefComplaintResponse,
        Error
    >(
        ['chief-complaints', debouncedSearchTerm],
        async ({ pageParam = 1 }) => {
            try {
                const res = await privateRequest.get(
                    `admin/chief-complaints?page=${pageParam}&query=${debouncedSearchTerm}`
                )
                return res.data.data
            } catch (error) {
                errorHandler(error)
            }
        },
        { getNextPageParam: (lastPage) => lastPage.nextPage }
    )

    const deleteMutation = useMutation<{ message: string }, Error, DeletePayload>(
        async ({ id }) => {
            try {
                const res = await privateRequest.delete(`admin/chief-complaints/${id}`)
                return res.data
            } catch (error) {
                errorHandler(error)
                throw error
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('chief-complaints')
            },
        }
    )

    const complaints = data?.pages?.flatMap((page) => page.docs) ?? []

    return (
        <div className="card">
            <div className="flex flex-wrap gap-4 mb-10">
                <Button
                    icon="add"
                    variant="outlined"
                    size='md'
                    onClick={() =>
                        addUpdateChiefComplaints.show({ title: 'Add New Chief Complaint' })
                    }
                >
                    Add New
                </Button>

                <Input
                    variant="outlined"
                    placeholder="Search"
                    size="md"
                    className="md:!w-[230px] ml-auto"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    afterFix={<BsSearch className="mr-3 text-gray" size={20} />}
                />
            </div>

            <div
                id="scrollableDiv"
                className="h-[calc(100vh-340px)] md:h-[calc(100vh-325px)] overflow-y-auto"
            >
                <InfiniteScroll
                    dataLength={complaints.length}
                    next={fetchNextPage}
                    hasMore={!!hasNextPage}
                    loader={
                        <div className="flex gap-2 justify-center items-center">
                            <LoaderIcon />
                            Loading...
                        </div>
                    }
                    scrollableTarget="scrollableDiv"
                >
                    <table className={clsx({ 'blur-sm animate-pulse': isLoading })}>
                        <thead>
                            <tr>
                                <td>Created Date</td>
                                <td>Name</td>
                                <td>Action</td>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading &&
                                Array(10)
                                    .fill(0)
                                    .map((_, i) => (
                                        <tr key={i}>
                                            <td>Day Month, Year</td>
                                            <td>Chief Complaint</td>
                                            <td />
                                        </tr>
                                    ))}

                            {complaints.map((row: ChiefComplaint) => (
                                <tr key={row._id}>
                                    <td>{dateFormatter(row.createdAt)}</td>
                                    <td>{row.name}</td>
                                    <td>
                                        <div className="inline-flex gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() =>
                                                    addUpdateChiefComplaints.show({
                                                        chiefComplaint: row,
                                                        title: 'Update Chief Complaint',
                                                    })
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                onClick={() => deleteMutation.mutate({ id: row._id })}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </InfiniteScroll>

                {!complaints.length && !isLoading && <NoData />}
            </div>
        </div>
    )
}
